import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { MOBILE } from '../../../utils/constants';
import { useState } from 'react';

interface Props {
  tags: string[];
  hiddenTagsLabel?: string;
}

const PostTags = ({ tags, hiddenTagsLabel = 'Hidden Tags' }: Props) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div css={containerStyles} data-testid="post-tags">
      {!isExpanded ? (
        <button css={toggleButtonStyles} onClick={handleToggle} data-testid="expand-tags-button">
          {hiddenTagsLabel} {'+'}
        </button>
      ) : (
        <div css={expandedContainerStyles}>
          <div css={tagsListStyles}>
            {tags.map((tag, index) => (
              <a
                key={tag}
                href={`/tags/${encodeURIComponent(tag)}`}
                css={tagStyles}
                onClick={(e) => e.preventDefault()}
                data-testid={`tag-${index}`}
              >
                {tag}
              </a>
            ))}
          </div>
          <button css={collapseButtonStyles} onClick={handleToggle} data-testid="collapse-tags-button">
            {'\u2212'}
          </button>
        </div>
      )}
    </div>
  );
};

const containerStyles = css({
  marginBottom: '24px',
  fontFamily: 'Inter, sans-serif',
  [MOBILE.MEDIA_QUERY]: {
    marginBottom: '16px',
  },
});

const toggleButtonStyles = css({
  backgroundColor: 'transparent',
  border: 'none',
  color: token('color.text.subtle'),
  fontSize: '14px',
  cursor: 'pointer',
  padding: '4px 0',
  textAlign: 'left',
  fontFamily: 'Inter, sans-serif',
  '&:hover': {
    color: token('color.text'),
    textDecoration: 'underline',
  },
  [MOBILE.MEDIA_QUERY]: {
    fontSize: '13px',
  },
});

const expandedContainerStyles = css({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '8px',
  [MOBILE.MEDIA_QUERY]: {
    gap: '6px',
  },
});

const tagsListStyles = css({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px',
  flex: 1,
  [MOBILE.MEDIA_QUERY]: {
    gap: '6px',
  },
});

const tagStyles = css({
  fontSize: '12px',
  color: token('color.text.subtle'),
  backgroundColor: token('color.background.neutral'),
  padding: '4px 8px',
  borderRadius: '4px',
  textDecoration: 'none',
  border: `1px solid ${token('color.border')}`,
  fontFamily: 'Inter, sans-serif',
  '&:hover': {
    backgroundColor: token('color.background.neutral.hovered'),
    color: token('color.text'),
    borderColor: token('color.border.focused'),
  },
  [MOBILE.MEDIA_QUERY]: {
    fontSize: '11px',
    padding: '3px 6px',
  },
});

const collapseButtonStyles = css({
  backgroundColor: 'transparent',
  border: 'none',
  color: token('color.text.subtle'),
  fontSize: '18px',
  cursor: 'pointer',
  padding: '2px 4px',
  lineHeight: 1,
  borderRadius: '2px',
  fontFamily: 'Inter, sans-serif',
  '&:hover': {
    color: token('color.text'),
    backgroundColor: token('color.background.neutral.hovered'),
  },
  [MOBILE.MEDIA_QUERY]: {
    fontSize: '16px',
    padding: '2px 2px',
  },
});

export default PostTags;
